/* ==========================================================================
   FurrFlora — interactions & motion
   ========================================================================== */
(() => {
  const $ = (s, el = document) => el.querySelector(s);
  const $$ = (s, el = document) => [...el.querySelectorAll(s)];
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

// Very old browsers without IntersectionObserver: show everything straight away.
if (!('IntersectionObserver' in window)) {
  window.IntersectionObserver = class {
    constructor(cb) { this.cb = cb; }
    observe(el) { setTimeout(() => this.cb([{ target: el, isIntersecting: true }], this), 60); }
    unobserve() {}
    disconnect() {}
  };
}

  /* ---------- page-load intro ---------- */
  const markLoaded = () => document.documentElement.classList.add('loaded');
  // wait briefly for fonts so the headline doesn't reflow mid-animation,
  // but never let the hero stay hidden (timers, not rAF, which pauses in background tabs)
  const fontsReady = document.fonts?.ready || Promise.resolve();
  Promise.race([fontsReady, new Promise((r) => setTimeout(r, 600))]).then(() => setTimeout(markLoaded, 30));
  setTimeout(markLoaded, 1500);

  /* ---------- header ---------- */
  const header = $('.site-header');
  const onScrollHeader = () => header.classList.toggle('scrolled', window.scrollY > 12);
  onScrollHeader();
  window.addEventListener('scroll', onScrollHeader, { passive: true });

  // Some hosts push the whole page down by the phone's top safe area (the Claude preview does: it pads the
  // page so nothing hides under the app's title bar), while this fixed header stays at the very top with its
  // own safe-area padding. The first section then added the same space again, leaving a tall empty band.
  // Measure how far the first section really starts below the header's top edge and take that off its
  // top spacing (--top-fix in styles.css). On an ordinary website the two line up and nothing changes.
  const leadSection = $('.banner, .page-hero, .pdp') || $('main');
  let topGapMeasured = false;
  const fixTopGap = () => {
    if (window.scrollY !== 0 || !leadSection) return; // only measurable with the page resting at the very top
    const push = Math.round(leadSection.getBoundingClientRect().top - header.getBoundingClientRect().top);
    document.documentElement.style.setProperty('--top-fix', push > 0 && push < 240 ? `${-push}px` : '0px');
    topGapMeasured = true;
  };
  fixTopGap();
  ['load', 'pageshow', 'orientationchange', 'resize'].forEach((ev) => window.addEventListener(ev, fixTopGap));
  window.addEventListener('scroll', () => { if (!topGapMeasured) fixTopGap(); }, { passive: true }); // page opened part-way down

  const menuBtn = $('[data-menu-toggle]');
  const navList = $('#nav-list');
  const setMenu = (open) => {
    menuBtn.setAttribute('aria-expanded', String(open));
    menuBtn.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
    navList.classList.toggle('open', open);
    header.classList.toggle('scrolled', open || window.scrollY > 12);
  };
  menuBtn.addEventListener('click', () => setMenu(menuBtn.getAttribute('aria-expanded') !== 'true'));
  navList.addEventListener('click', (e) => { if (e.target.closest('a')) setMenu(false); });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') setMenu(false); });

  /* ---------- scroll reveal ---------- */
  const revealObserver = new IntersectionObserver((entries) => {
    entries.forEach((en) => {
      if (en.isIntersecting) { en.target.classList.add('in'); revealObserver.unobserve(en.target); }
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

  const observeReveals = (root = document) => {
    $$('.reveal:not(.in)', root).forEach((el, i) => {
      // gentle stagger for siblings revealed together
      if (!el.style.getPropertyValue('--rd')) {
        const sibs = [...el.parentElement.children].filter((c) => c.classList.contains('reveal'));
        el.style.setProperty('--rd', `${Math.min(sibs.indexOf(el), 5) * 0.08}s`);
      }
      revealObserver.observe(el);
    });
  };
  window.FF = { observeReveals };
  observeReveals();

  /* ---------- pause off-screen animated scenes ---------- */
  const sceneObserver = new IntersectionObserver((entries) => {
    entries.forEach((en) => en.target.classList.toggle('is-offscreen', !en.isIntersecting));
  }, { rootMargin: '100px' });
  // every section pauses its animations while it is off screen (saves battery, keeps phones smooth)
  $$('[data-scene], .hero-sky, .night-stars, .marquee, main > section, main > .shopify-section > section, .paw-trail, .site-footer').forEach((el) => sceneObserver.observe(el));

  /* ---------- peeking pets: pop up when their card scrolls into view ---------- */
  const peekObserver = new IntersectionObserver((entries) => {
    entries.forEach((en) => {
      if (en.isIntersecting) {
        // small stagger so neighbouring pets don't all pop at once
        const delay = [...document.querySelectorAll('[data-peek]:not(.peeked)')].indexOf(en.target) % 3;
        setTimeout(() => en.target.classList.add('peeked'), 120 + delay * 160);
        peekObserver.unobserve(en.target);
      }
    });
  }, { threshold: 0.35 });
  $$('[data-peek]').forEach((el) => {
    peekObserver.observe(el);
    sceneObserver.observe(el);
  });

  // Backup for devices where the observer fires late: check positions on scroll/load too.
  let peekCheckQueued = false;
  const checkPeeks = () => {
    peekCheckQueued = false;
    const vh = window.innerHeight;
    // measure everything first, then change classes, so the page is laid out once (smoother on phones)
    const peeks = $$('[data-peek]:not(.peeked)').filter((el) => { const r = el.getBoundingClientRect(); return r.top < vh * 0.9 && r.bottom > 0; });
    const reveals = $$('.reveal:not(.in)').filter((el) => { const r = el.getBoundingClientRect(); return r.top < vh * 0.95 && r.bottom > 0; });
    peeks.forEach((el) => el.classList.add('peeked'));
    reveals.forEach((el) => el.classList.add('in'));
    if (!document.querySelector('[data-peek]:not(.peeked), .reveal:not(.in)')) {
      window.removeEventListener('scroll', queuePeekCheck);
      window.removeEventListener('resize', queuePeekCheck);
    }
  };
  const queuePeekCheck = () => { if (!peekCheckQueued) { peekCheckQueued = true; setTimeout(checkPeeks, 150); } };
  window.addEventListener('scroll', queuePeekCheck, { passive: true });
  window.addEventListener('resize', queuePeekCheck);
  window.addEventListener('load', () => setTimeout(checkPeeks, 700));
  setTimeout(checkPeeks, 1600);
  // Last-resort sweep for slow devices; stops itself once everything has appeared.
  const sweep = setInterval(() => {
    if (!document.querySelector('[data-peek]:not(.peeked), .reveal:not(.in)')) { clearInterval(sweep); return; }
    checkPeeks();
  }, 900);

  // tap / click a pet for a little boop + heart
  const heartSVG = '<svg viewBox="0 0 24 24"><path d="M12 21s-7.5-4.6-9.5-9.3C1 8.2 3.2 4.5 6.8 4.5c2.1 0 3.6 1.2 5.2 3 1.6-1.8 3.1-3 5.2-3 3.6 0 5.8 3.7 4.3 7.2C19.5 16.4 12 21 12 21Z"/></svg>';
  document.addEventListener('click', (e) => {
    const head = e.target.closest('.peek-img, .peek-head');
    if (!head) return;
    const peek = head.closest('.peek');
    head.classList.remove('boop'); void head.getBoundingClientRect(); head.classList.add('boop');
    setTimeout(() => head.classList.remove('boop'), 650);
    if (reduceMotion.matches) return;
    const heart = document.createElement('span');
    heart.className = 'heart';
    heart.innerHTML = heartSVG;
    const pr = peek.getBoundingClientRect();
    heart.style.left = `${e.clientX - pr.left}px`;
    heart.style.top = `${e.clientY - pr.top - 20}px`;
    peek.appendChild(heart);
    setTimeout(() => heart.remove(), 1200);
  });

  /* ---------- mobile sticky buy bar ---------- */
  const buyBar = $('[data-buy-bar]');
  const hero = $('.hero');
  const shopSection = $('#shop');
  const footer = $('.site-footer');
  if (buyBar && hero && footer) {
    let pastHero = false, nearShop = false;
    const sync = () => buyBar.classList.toggle('show', pastHero && !nearShop);
    new IntersectionObserver(([en]) => { pastHero = !en.isIntersecting; sync(); }, { threshold: 0.05 }).observe(hero);
    const nearObs = new IntersectionObserver((entries) => {
      entries.forEach((en) => { en.target.dataset.near = en.isIntersecting ? '1' : ''; });
      nearShop = (shopSection && shopSection.dataset.near === '1') || footer.dataset.near === '1';
      sync();
    });
    if (shopSection) nearObs.observe(shopSection);
    nearObs.observe(footer);
  }

/* ---------- announcement bar: seamless loop at a steady reading speed ---------- */
const announce = $('[data-announce]');
if (announce) {
  const base = $('.announce-group', announce);
  const unit = base.innerHTML;
  const SPEED = 55; // pixels per second: an easy reading pace on any screen
  const build = () => {
    $$('.announce-group', announce).slice(1).forEach((g) => g.remove());
    base.innerHTML = unit;
    // repeat the messages until one group is wider than the screen, then add a twin for the loop
    let guard = 0;
    while (base.scrollWidth < window.innerWidth + 40 && guard++ < 10) base.insertAdjacentHTML('beforeend', unit);
    announce.appendChild(base.cloneNode(true));
    announce.style.setProperty('--announce-dur', `${(base.scrollWidth / SPEED).toFixed(1)}s`);
  };
  build();
  let resizeTimer;
  let lastW = window.innerWidth;
  window.addEventListener('resize', () => {
    if (Math.abs(window.innerWidth - lastW) < 40) return; // ignore mobile address-bar wobble
    lastW = window.innerWidth;
    clearTimeout(resizeTimer); resizeTimer = setTimeout(build, 200);
  });
}

/* ---------- marquee: duplicate items for a seamless loop ---------- */
  const track = $('[data-marquee]');
  if (track) {
    $$('li', track).forEach((li) => {
      const clone = li.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      track.appendChild(clone);
    });
  }

  /* ---------- paw trail ---------- */
  const trail = $('[data-trail]');
  if (trail) {
    new IntersectionObserver(([en]) => trail.classList.toggle('walking', en.isIntersecting), { threshold: 0.3 })
      .observe(trail);
  }

  /* ---------- how-it-works: scroll-linked progress ---------- */
  const how = $('[data-progress-section]');
  const moodEl = $('[data-mood]');
  const steps = how ? $$('.step', how) : [];
  const moods = ['On edge', 'Curious', 'Settling', 'Blissed out'];
  let ticking = false;
  let lastMood = '';

  const updateHow = () => {
    ticking = false;
    // read every position first, then write (one layout per frame instead of several)
    const r = how.getBoundingClientRect();
    const vh = window.innerHeight;
    const mid = vh * 0.55;
    const stepTops = steps.map((s) => s.getBoundingClientRect().top);
    // 0 when the section top reaches 60% of the viewport, 1 near its end
    const start = vh * 0.6;
    const total = r.height - vh * 0.5;
    const p = Math.min(1, Math.max(0, (start - r.top) / total));
    const pText = p.toFixed(3);
    if (pText !== lastP) { how.style.setProperty('--p', pText); lastP = pText; }

    const mood = moods[Math.min(moods.length - 1, Math.floor(p * moods.length))];
    if (mood !== lastMood) { moodEl.textContent = mood; lastMood = mood; }

    // the active step is whichever one is nearest the viewport's center
    let active = 0;
    stepTops.forEach((top, i) => { if (top < mid) active = i; });
    steps.forEach((s, i) => s.classList.toggle('active', i <= active && p > 0.02));
  };
  let lastP = '';
  const requestHow = () => { if (!ticking) { ticking = true; requestAnimationFrame(updateHow); } };
  if (how) {
    updateHow();
    // only follow the scroll while the section is on (or near) the screen
    let following = false;
    new IntersectionObserver(([en]) => {
      if (en.isIntersecting === following) return;
      following = en.isIntersecting;
      if (following) { window.addEventListener('scroll', requestHow, { passive: true }); requestHow(); }
      else { window.removeEventListener('scroll', requestHow); requestHow(); }
    }, { rootMargin: '200px 0px' }).observe(how);
    window.addEventListener('resize', requestHow);
  }

  /* ---------- cat / dog switch (site-wide theme) ---------- */
  const PET_KEY = 'furrflora-pet';
  const setPet = (pet, { announce = true } = {}) => {
    document.body.dataset.pet = pet;
    $$('[data-pet-choice]').forEach((b) => b.setAttribute('aria-checked', String(b.dataset.petChoice === pet)));
    $$('[data-pane]').forEach((p) => { p.hidden = p.dataset.pane !== pet; });
    const cta = $('[data-formula-cta]');
    if (cta) cta.textContent = pet === 'dog' ? 'Shop Canine Formula' : 'Shop Feline Formula';
    $$('[data-pet-name]').forEach((el) => { el.textContent = pet === 'dog' ? 'Canine' : 'Feline'; });
    $$('[data-pet-other]').forEach((el) => { el.textContent = pet === 'dog' ? 'cat' : 'dog'; });
    try { localStorage.setItem(PET_KEY, pet); } catch { /* ignore */ }
    if (announce) document.dispatchEvent(new CustomEvent('ff:petchange', { detail: pet }));
  };
  let savedPet = null;
  try { savedPet = localStorage.getItem(PET_KEY); } catch { /* ignore */ }
  if (savedPet === 'dog' || savedPet === 'cat') setPet(savedPet);

  $$('[data-pet-choice]').forEach((b) => b.addEventListener('click', () => setPet(b.dataset.petChoice)));
  // arrow-key support for the radiogroup
  $('.pet-switch')?.addEventListener('keydown', (e) => {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) return;
    e.preventDefault();
    const next = document.body.dataset.pet === 'cat' ? 'dog' : 'cat';
    setPet(next);
    $(`[data-pet-choice="${next}"]`).focus();
  });
  $$('[data-pet-swap]').forEach((b) => b.addEventListener('click', () => setPet(document.body.dataset.pet === 'cat' ? 'dog' : 'cat')));

  /* ---------- reviews carousel ---------- */
  const carousel = $('[data-carousel]');
  const scrollByCard = (dir) => {
    const card = $('.review', carousel);
    const step = card ? card.getBoundingClientRect().width + 18 : 320;
    carousel.scrollBy({ left: dir * step, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
  };
  $('[data-carousel-prev]')?.addEventListener('click', () => scrollByCard(-1));
  $('[data-carousel-next]')?.addEventListener('click', () => scrollByCard(1));
  carousel?.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowRight') { e.preventDefault(); scrollByCard(1); }
    if (e.key === 'ArrowLeft') { e.preventDefault(); scrollByCard(-1); }
  });

  /* ---------- FAQ: keep one open at a time ---------- */
  const faqs = $$('.accordion details');
  faqs.forEach((d) => d.addEventListener('toggle', () => {
    if (d.open) faqs.forEach((o) => { if (o !== d) o.open = false; });
  }));

  /* ---------- newsletter ---------- */
  // Connect to your email provider (e.g. Shopify Customer API, Klaviyo) where noted.
  const nl = $('[data-newsletter]');
  nl?.addEventListener('submit', (e) => {
    e.preventDefault();
    const input = $('input[type=email]', nl);
    const msg = $('.nl-msg', nl);
    const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.value.trim());
    msg.classList.toggle('error', !ok);
    input.setAttribute('aria-invalid', String(!ok));
    if (!ok) { msg.textContent = 'Please enter a valid email address, like you@example.com.'; input.focus(); return; }
    // sends the address to the mailing list set up in welcome.js (WELCOME_CONFIG.signupUrl)
    window.ffSubscribe?.(input.value.trim(), 'footer');
    try { localStorage.setItem('furrflora-welcome', 'joined'); } catch { /* private mode */ }
    window.ffShowGift?.();
    const w = window.ffWelcomeCode;
    msg.textContent = w ? `You’re on the list! Use code ${w.code} for ${w.percent}% off your first order. It’s added at checkout automatically.` : 'You’re on the list! Check your inbox for 15% off.';
    document.dispatchEvent(new CustomEvent('ff:discount'));
    nl.reset();
  });

/* ---------- contact form ---------- */
// On the live site (hosted on Netlify) the message is sent straight from the page through Netlify Forms.
// Anywhere else (a preview, or a copy opened from a computer) it opens the visitor's email app with the
// message filled in. To use a different form service instead, paste its address into `endpoint`.
const CONTACT_CONFIG = {
  endpoint: 'https://formspree.io/f/mdeapaao', // Formspree (free plan), delivers to furrflora@gmail.com. Empty it to fall back to Netlify Forms or the email app.
  netlifyHosts: ['furrflora.com', 'netlify.app'], // addresses where Netlify receives the form
};
const contactViaNetlify = !CONTACT_CONFIG.endpoint && CONTACT_CONFIG.netlifyHosts.some((h) => location.hostname === h || location.hostname.endsWith(`.${h}`));
const contactTarget = CONTACT_CONFIG.endpoint || (contactViaNetlify ? '/' : '');
// the website's form has data-contact-form; in the Shopify theme it is Shopify's own contact form (#contact-form)
const contactForm = $('[data-contact-form], #contact-form');
contactForm?.addEventListener('submit', async (e) => {
  const status = $('[data-contact-status]', contactForm);
  const say = (kind, text) => { status.className = `form-status ${kind}`; status.textContent = text; };
  let firstBad = null;
  $$('[data-required]', contactForm).forEach((field) => {
    const v = field.value.trim();
    const bad = !v || (field.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v));
    field.setAttribute('aria-invalid', String(bad));
    const err = $(`#${field.id}-err`, contactForm);
    if (err) err.textContent = bad ? (field.type === 'email' ? 'Please enter a valid email, like you@example.com.' : 'This field is required.') : '';
    if (bad && !firstBad) firstBad = field;
  });
  if (firstBad) { e.preventDefault(); say('error', 'Please check the highlighted fields.'); firstBad.focus(); return; }
  // Shopify theme: Shopify sends the message itself (and adds its own spam protection), so let the form submit
  if (window.FF_THEME) { say('', 'Sending your message…'); return; }
  e.preventDefault();
  const data = Object.fromEntries(new FormData(contactForm));
  const to = contactForm.dataset.to || 'furrflora@gmail.com'; // where the email-app fallback is addressed; not printed on the page
  const subject = `[${data.topic}] Message from ${data.name}`;
  const body = `${data.message}

— ${data.name} (${data.email})${data.order ? `
Order: ${data.order}` : ''}`;
  const mailto = `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

  if (!contactTarget) {
    window.location.href = mailto;
    say('ok', `Thanks, ${data.name}! Your email app should open with your message ready to send. Just press send there.`);
    return;
  }
  if (data._gotcha) { say('ok', 'Thanks! Your message is on its way.'); contactForm.reset(); return; } // hidden field: only bots fill it

  const btn = $('[type="submit"]', contactForm);
  const label = btn.textContent;
  btn.disabled = true;
  btn.textContent = 'Sending…';
  say('', 'Sending your message…');
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 15000);
  try {
    const subjectField = contactForm.elements.subject;
    if (subjectField) subjectField.value = subject; // the subject line of the email you receive
    let payload = new FormData(contactForm);
    if (contactViaNetlify) {
      payload.set('form-name', contactForm.getAttribute('name')); // Netlify also adds this field on upload; set, not append, so it is sent once
      payload = new URLSearchParams(payload); // Netlify Forms expects this encoding
    } else payload.append('_subject', subject);
    const res = await fetch(contactTarget, { method: 'POST', headers: { Accept: 'application/json' }, body: payload, signal: ctrl.signal });
    if (!res.ok) throw new Error(`Form service answered ${res.status}`);
    say('ok', `Thanks, ${data.name}! Your message is on its way. We’ll reply to ${data.email}.`);
    contactForm.reset();
  } catch {
    // nothing is lost: what they typed stays in the form, and the link opens it in their email app
    say('error', 'Sorry, that didn’t send. Please try again, or ');
    const link = document.createElement('a');
    link.href = mailto;
    link.textContent = 'send it from your email app';
    status.append(link, '.');
  } finally {
    clearTimeout(timer);
    btn.disabled = false;
    btn.textContent = label;
  }
});

// after Shopify's contact form is sent it comes back with ?contact_posted=true: show the thanks on the contact page
if (/[?&]contact_posted=true/.test(location.search)) {
  if (contactForm) {
    const st = $('[data-contact-status]', contactForm);
    if (st && !st.textContent.trim()) { st.className = 'form-status ok'; st.textContent = 'Thanks! Your message is on its way. We’ll reply within one business day.'; }
  } else if (window.FF_THEME) location.replace('/?view=contact&contact_posted=true#contact-form');
}

/* ---------- highlight the current page in the menu ---------- */
let page = (location.pathname.split('/').pop() || '/').toLowerCase();
if (page === '/?view=item') page = '/collections/all'; // product pages live under Shop
if (window.FF_THEME) page = document.body.dataset.page || '/'; // Shopify theme: the layout says which page this is
$$('.nav-list a, .footer-links a').forEach((a) => {
  let target = (a.getAttribute('href') || '').split('#')[0].toLowerCase();
  if (target === './') target = '/';
  if (target && target === page && !a.getAttribute('href').includes('#')) a.setAttribute('aria-current', 'page');
});

/* ---------- misc ---------- */
  $$('[data-year]').forEach((el) => { el.textContent = new Date().getFullYear(); });
})();
